import hash from 'hash.js'
import { scalarMult, secretbox, box, randomBytes } from "tweetnacl";
import {
  decodeUTF8,
  encodeUTF8,
  encodeBase64,
  decodeBase64
} from "tweetnacl-util";

const crypto = (function () {

  const cryptoServices = {
    generateAKey,
    aKeyCheck,
    aKeyEncrypt,
    aKeyEncrypt_str,
    aKeyDecrypt,
    aKeyDecrypt_str,
    generateSKey,
    sKeyEncrypt,
    sKeyDecrypt,
    uint8ArrayToBase64,
    base64ToUint8Array,
    uTF8ToUint8Array,
    uint8ArrayToUTF8,
  };

  /**
   * returns a public, and secret key pair for asymmetric encrypting
   */
  function generateAKey() {
    return box.keyPair()
  };

  /**
   * 
   * @param publicKey 
   * @param secretKey 
   * 
   * checks if the inputs are a valid KeyPair
   */
  function isUint8ArrayEqual(aa: Uint8Array, bb: Uint8Array): boolean {
    if (aa.length != bb.length) {
      return false;
    }
    for (let ii = 0; ii < aa.length; ii++) {
      if (aa[ii] != bb[ii])
        return false;
    }
    return true;
  }

  function aKeyCheck(publicKey: Uint8Array, secretKey: Uint8Array): boolean {
    return isUint8ArrayEqual(publicKey, scalarMult.base(secretKey));
  }

  /**
   * 
   * @param secretKey
   * @param data 
   * 
   * assymetrically encrypts data using the sender's secretKey
   * and receiver's publicKey
   * extraKey provide extra protection which needs to be exchanged separatedly
   */
  function aKeyEncrypt(
    recvPublicKey: Uint8Array,
    sendSecretKey: Uint8Array,
    data: Uint8Array,
    extraKey?: Uint8Array): Uint8Array {
    const nonce = newNonce();
    const sharedKey = box.before(recvPublicKey, sendSecretKey);
    const encrypted = extraKey
      ? box(data, nonce, extraKey, sharedKey)
      : box.after(data, nonce, sharedKey);

    const fullMessage = new Uint8Array(nonce.length + encrypted.length);
    fullMessage.set(nonce);
    fullMessage.set(encrypted, nonce.length);
    return fullMessage;
  }

  function aKeyEncrypt_str(
    recvPublicKey: Uint8Array,
    sendSecretKey: Uint8Array,
    data: string,
    extraKey?: string): string {
    const fullMessage = extraKey ?
      aKeyEncrypt(recvPublicKey, sendSecretKey,
        decodeUTF8(data),
        new Uint8Array(hash.sha256().update(extraKey).digest()))
      : aKeyEncrypt(recvPublicKey, sendSecretKey,
        decodeUTF8(data));
    return encodeBase64(fullMessage);
  }

  /**
   * 
   * @param publicKey 
   * @param eData 
   * 
   * decrypts assymetrically encrypted data using the sender's publicKey
   * and receiver's secretKey
   * extraKey:may needed
   */
  function aKeyDecrypt(
    sendPublicKey: Uint8Array,
    recvSecretKey: Uint8Array,
    eData: Uint8Array,
    extraKey?: Uint8Array): Uint8Array | null {
    const sharedKey = box.before(sendPublicKey, recvSecretKey);
    const nonce = eData.slice(0, box.nonceLength);
    const message = eData.slice(box.nonceLength, eData.length);

    const decrypted = extraKey
      ? box.open(message, nonce, extraKey, sharedKey)
      : box.open.after(message, nonce, sharedKey);

    /*
        if (!decrypted) {
        throw new Error('Could not decrypt message');
    }
    */
    return decrypted;
  }

  function aKeyDecrypt_str(
    sendPublicKey: Uint8Array,
    recvSecretKey: Uint8Array,
    eData: string, // any string
    extraKey?: string // base64 encoded byte array
  ): string | null {
    const eDataArr = decodeBase64(eData);
    console.log(eDataArr.length);
    const decrypted = extraKey ?
      aKeyDecrypt(sendPublicKey, recvSecretKey, eDataArr,
        new Uint8Array(hash.sha256().update(extraKey).digest()))
      : aKeyDecrypt(sendPublicKey, recvSecretKey, eDataArr);
    if (!decrypted) {
      console.log("fail to decrypt!");
      return "";
    }
    return encodeUTF8(decrypted);
  }
  /**
   * returns a secretKey for symmetric encrypting 
   */
  function generateSKey(): Uint8Array {
    const secretKey: Uint8Array = randomBytes(secretbox.keyLength);
    return secretKey;
  }

  /**
   * 
   * @param secretKey 
   * @param data 
   * 
   * encrypts data symmetrically using teh provided secretKey
   */
  function sKeyEncrypt(secretKey: Uint8Array, data: Uint8Array): Uint8Array {
    const nonce: Uint8Array = randomBytes(secretbox.nonceLength);
    const box: Uint8Array = secretbox(data, nonce, secretKey);
    const encryptedData: Uint8Array = new Uint8Array(nonce.length + box.length);
    encryptedData.set(nonce);
    encryptedData.set(box, nonce.length);
    return encryptedData;
  }

  /**
   * 
   * @param secretKey 
   * @param eData 
   * 
   * decrypt data symmetrically using the secretKey provided
   */
  function sKeyDecrypt(secretKey: Uint8Array, eData: Uint8Array): Uint8Array {
    const nonce: Uint8Array = eData.slice(
      0,
      secretbox.nonceLength
    );
    const data: Uint8Array = eData.slice(
      secretbox.nonceLength,
      eData.length
    );
    const decryptedData: Uint8Array | null = secretbox.open(data, nonce, secretKey);
    if (!decryptedData) {
      throw new Error("Could not decrypt data");
    }
    return decryptedData;
  }

  /**
   * 
   * @param data 
   * 
   * encodes Uint8Array data to base64 string
   */
  function uint8ArrayToBase64(data: Uint8Array): string {
    return encodeBase64(data)
  }

  /**
   * 
   * @param data 
   * 
   * decodes string data to Uint8Array
   */
  function base64ToUint8Array(data: string): Uint8Array {
    return decodeBase64(data)
  }
  /**
   * 
   * @param data 
   * 
   * decodes string and returns Uint8Array
   */
  function uTF8ToUint8Array(data: string): Uint8Array {
    return decodeUTF8(data)
  }

  /**
   * 
   * @param data 
   * 
   * encodes Uint8Array or Array of bytes into string
   */
  function uint8ArrayToUTF8(data: Uint8Array): string {
    return encodeUTF8(data)
  }

  /**
   * creates random nonce
   */
  function newNonce() {
    return randomBytes(box.nonceLength)
  }

  return cryptoServices
}())

export default crypto
